import React, { useState, useEffect, useRef } from 'react';
import { getStoredUser, saveAuthSession } from '../utils/authStorage';
import { saveProfile, getStoredProfile } from '../utils/profileStorage';
import { 
  getUserById, 
  updateUser, 
  changePassword,
  createApplicationPassword,
  getApplicationPasswords,
  revokeApplicationPassword,
  revokeAllApplicationPasswords
} from '../api/userService';
import { useTheme } from '../contexts/ThemeContext';
import './UserProfile.css';

const UserProfile = () => {
  const currentUser = getStoredUser();
  const { updateTheme, updateSyntaxHighlighting } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fileInputRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Application Password state
  const [applicationPasswords, setApplicationPasswords] = useState([]);
  const [newAppPasswordName, setNewAppPasswordName] = useState('');
  const [creatingAppPassword, setCreatingAppPassword] = useState(false);
  const [newlyCreatedPassword, setNewlyCreatedPassword] = useState(null);
  const [revokingPasswordId, setRevokingPasswordId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Options
    disableSyntaxHighlighting: false,
    adminColorScheme: 'default',
    enableKeyboardShortcuts: false,
    showToolbar: true,
    language: 'site-default',
    
    // Name
    username: '',
    firstName: '',
    lastName: '',
    nickname: '',
    displayName: '',
    
    // Contact Info
    email: '',
    website: '',
    
    // About Yourself
    profilePicture: '',
  });

  useEffect(() => {
    // Reset form data to initial state before loading
    setFormData({
      disableSyntaxHighlighting: false,
      adminColorScheme: 'default',
      enableKeyboardShortcuts: false,
      showToolbar: true,
      language: 'site-default',
      username: '',
      firstName: '',
      lastName: '',
      nickname: '',
      displayName: '',
      email: '',
      website: '',
      profilePicture: '',
    });
    setAvatarPreview(null);
    setMessage({ type: '', text: '' });
    
    // Then load fresh data from API
    loadUserProfile();
    loadApplicationPasswords();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const response = await getUserById(currentUser.id || currentUser._id);
      const userData = response.user;
      setUser(userData);
      setFormData({
        disableSyntaxHighlighting: userData.disableSyntaxHighlighting || false,
        adminColorScheme: userData.adminColorScheme || 'default',
        enableKeyboardShortcuts: userData.enableKeyboardShortcuts || false,
        showToolbar: userData.showToolbar !== undefined ? userData.showToolbar : true,
        language: userData.language || 'site-default',
        username: userData.username || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        nickname: userData.nickname || userData.username || '',
        displayName: userData.displayName || userData.fullName || '',
        email: userData.email || '',
        website: userData.website || '',
        profilePicture: userData.profilePicture || '',
      });
      
      // Set avatar preview if profile picture exists
      if (userData.profilePicture) {
        setAvatarPreview(userData.profilePicture);
      } else {
        setAvatarPreview(null);
      }

      // Update stored user data with latest profile picture
      const updatedUser = {
        ...currentUser,
        profilePicture: userData.profilePicture || '',
      };
      saveAuthSession({ user: updatedUser });
      
      // Sync profile picture to profile storage (always sync, even if empty)
      const currentProfile = getStoredProfile();
      saveProfile({ ...currentProfile, avatar: userData.profilePicture || '' });
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Apply syntax highlighting setting immediately when checkbox changes
    if (field === 'disableSyntaxHighlighting' && updateSyntaxHighlighting) {
      updateSyntaxHighlighting(value);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image size must be less than 5MB');
      return;
    }

    // Create preview and convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setAvatarPreview(base64String);
      setFormData((prev) => ({ ...prev, profilePicture: base64String }));
      setAvatarError('');
    };
    reader.onerror = () => {
      setAvatarError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveProfilePicture = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      // Clear profile picture in form data
      setFormData((prev) => ({ ...prev, profilePicture: '' }));
      setAvatarPreview(null);
      
      // Update backend
      const response = await updateUser(currentUser.id || currentUser._id, { profilePicture: '' });
      
      // Update stored user data
      const updatedUser = {
        ...currentUser,
        profilePicture: '',
      };
      saveAuthSession({ user: updatedUser });
      
      // Update profile storage
      const currentProfile = getStoredProfile();
      saveProfile({ ...currentProfile, avatar: '' });
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('profile-updated'));
      window.dispatchEvent(new Event('userUpdated'));
      
      setMessage({ type: 'success', text: 'Profile picture removed successfully.' });
      
      // Reload user data
      await loadUserProfile();
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to remove profile picture. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    if (formData.firstName && formData.lastName) {
      return `${formData.firstName.charAt(0)}${formData.lastName.charAt(0)}`.toUpperCase();
    }
    if (formData.firstName) {
      return formData.firstName.charAt(0).toUpperCase();
    }
    if (formData.username) {
      return formData.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const generateStrongPassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 20; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
  };

  const calculatePasswordStrength = (password) => {
    if (!password) return { strength: 'none', label: '', color: '#ddd' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    if (password.length >= 16) strength++;

    if (strength <= 2) {
      return { strength: 'weak', label: 'Weak', color: '#d63638' };
    } else if (strength <= 4) {
      return { strength: 'medium', label: 'Medium', color: '#f0b849' };
    } else {
      return { strength: 'strong', label: 'Strong', color: '#00a32a' };
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.trim() === '') {
      setMessage({ type: 'error', text: 'Please enter a new password.' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }

    try {
      setChangingPassword(true);
      setMessage({ type: '', text: '' });
      
      await changePassword(currentUser.id || currentUser._id, newPassword);
      
      setMessage({ type: 'success', text: 'Password changed successfully.' });
      setNewPassword('');
      setShowPasswordForm(false);
      setShowPassword(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password. Please try again.',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelPassword = () => {
    setNewPassword('');
    setShowPasswordForm(false);
    setShowPassword(false);
    setMessage({ type: '', text: '' });
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  // Application Password functions
  const loadApplicationPasswords = async () => {
    try {
      const response = await getApplicationPasswords(currentUser.id || currentUser._id);
      if (response.success) {
        setApplicationPasswords(response.applicationPasswords || []);
      }
    } catch (error) {
      console.error('Failed to load application passwords:', error);
    }
  };

  const handleCreateApplicationPassword = async () => {
    if (!newAppPasswordName || !newAppPasswordName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a name for the application password.' });
      return;
    }

    try {
      setCreatingAppPassword(true);
      setMessage({ type: '', text: '' });
      
      const response = await createApplicationPassword(
        currentUser.id || currentUser._id,
        newAppPasswordName.trim()
      );
      
      if (response.success) {
        setNewlyCreatedPassword(response.applicationPassword);
        setNewAppPasswordName('');
        setMessage({ type: 'success', text: 'Application password created successfully. Make sure to copy it now - you won\'t be able to see it again!' });
        await loadApplicationPasswords();
      }
    } catch (error) {
      console.error('Failed to create application password:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to create application password. Please try again.',
      });
    } finally {
      setCreatingAppPassword(false);
    }
  };

  const handleRevokeApplicationPassword = async (passwordId) => {
    if (!window.confirm('Are you sure you want to revoke this application password?')) {
      return;
    }

    try {
      setRevokingPasswordId(passwordId);
      setMessage({ type: '', text: '' });
      
      const response = await revokeApplicationPassword(
        currentUser.id || currentUser._id,
        passwordId
      );
      
      if (response.success) {
        setMessage({ type: 'success', text: 'Application password revoked successfully.' });
        await loadApplicationPasswords();
        // Clear newly created password if it was revoked
        if (newlyCreatedPassword && newlyCreatedPassword.id === passwordId) {
          setNewlyCreatedPassword(null);
        }
      }
    } catch (error) {
      console.error('Failed to revoke application password:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to revoke application password. Please try again.',
      });
    } finally {
      setRevokingPasswordId(null);
    }
  };

  const handleRevokeAllApplicationPasswords = async () => {
    if (!window.confirm('Are you sure you want to revoke all application passwords? This action cannot be undone.')) {
      return;
    }

    try {
      setRevokingAll(true);
      setMessage({ type: '', text: '' });
      
      const response = await revokeAllApplicationPasswords(currentUser.id || currentUser._id);
      
      if (response.success) {
        setMessage({ type: 'success', text: `${response.revokedCount} application password(s) revoked successfully.` });
        await loadApplicationPasswords();
        setNewlyCreatedPassword(null);
      }
    } catch (error) {
      console.error('Failed to revoke all application passwords:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to revoke application passwords. Please try again.',
      });
    } finally {
      setRevokingAll(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const response = await updateUser(currentUser.id || currentUser._id, formData);
      
      // Update profile picture in stored user data and profile storage
      if (formData.profilePicture !== undefined) {
        const updatedUser = {
          ...currentUser,
          profilePicture: formData.profilePicture || '',
        };
        saveAuthSession({ user: updatedUser });
        
        // Update profile storage
        const currentProfile = getStoredProfile();
        saveProfile({ ...currentProfile, avatar: formData.profilePicture || '' });
        
        // Dispatch event to notify other components
        window.dispatchEvent(new Event('profile-updated'));
      }
      
      // Update theme immediately if color scheme changed
      if (formData.adminColorScheme) {
        updateTheme(formData.adminColorScheme);
        
        // Update stored user data with new color scheme
        const updatedUser = {
          ...currentUser,
          adminColorScheme: formData.adminColorScheme,
        };
        saveAuthSession({ user: updatedUser });
        
        // Dispatch event to notify theme context
        window.dispatchEvent(new Event('userUpdated'));
      }

      // Update syntax highlighting preference immediately
      if (formData.disableSyntaxHighlighting !== undefined && updateSyntaxHighlighting) {
        updateSyntaxHighlighting(formData.disableSyntaxHighlighting);
        
        // Update stored user data with new syntax highlighting preference
        const updatedUser = {
          ...currentUser,
          disableSyntaxHighlighting: formData.disableSyntaxHighlighting,
        };
        saveAuthSession({ user: updatedUser });
        
        // Dispatch event to notify theme context
        window.dispatchEvent(new Event('userUpdated'));
      }
      
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      
      // Reload user data
      await loadUserProfile();
    } catch (error) {
      console.error('Failed to update profile:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wp-profile-container">
        <h1 className="wp-page-title">Profile</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="wp-profile-container">
      <h1 className="wp-page-title">Profile</h1>

      {message.text && (
        <div className={`wp-notice wp-notice-${message.type}`}>
          <p>{message.text}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Name */}
        <h2 className="wp-section-title">Name</h2>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th>
                <label htmlFor="username">Username</label>
              </th>
              <td>
                <input
                  type="text"
                  id="username"
                  className="wp-form-input"
                  value={formData.username}
                  disabled
                />
                <p className="wp-info-text">Usernames cannot be changed.</p>
              </td>
            </tr>
            <tr>
              <th>
                <label htmlFor="firstName">First Name</label>
              </th>
              <td>
                <input
                  type="text"
                  id="firstName"
                  className="wp-form-input"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>
                <label htmlFor="lastName">Last Name</label>
              </th>
              <td>
                <input
                  type="text"
                  id="lastName"
                  className="wp-form-input"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>
                <label htmlFor="nickname">Nickname <span className="wp-required">*</span></label>
              </th>
              <td>
                <input
                  type="text"
                  id="nickname"
                  className="wp-form-input"
                  value={formData.nickname}
                  onChange={(e) => handleInputChange('nickname', e.target.value)}
                  required
                />
              </td>
            </tr>
            <tr>
              <th>
                <label htmlFor="displayName">Display name publicly as</label>
              </th>
              <td>
                <select
                  id="displayName"
                  className="wp-form-select"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                >
                  <option value={formData.username}>{formData.username}</option>
                  {formData.firstName && <option value={formData.firstName}>{formData.firstName}</option>}
                  {formData.lastName && <option value={formData.lastName}>{formData.lastName}</option>}
                  {formData.firstName && formData.lastName && (
                    <option value={`${formData.firstName} ${formData.lastName}`}>
                      {formData.firstName} {formData.lastName}
                    </option>
                  )}
                  {formData.nickname && <option value={formData.nickname}>{formData.nickname}</option>}
                </select>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Contact Info */}
        <h2 className="wp-section-title">Contact Info</h2>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th>
                <label htmlFor="email">Email <span className="wp-required">*</span></label>
              </th>
              <td>
                <input
                  type="email"
                  id="email"
                  className="wp-form-input"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
                <p className="wp-info-text">
                  If you change this, an email will be sent at your new address to confirm it. The new address will not become active until confirmed.
                </p>
              </td>
            </tr>
            <tr>
              <th>
                <label htmlFor="website">Website</label>
              </th>
              <td>
                <input
                  type="url"
                  id="website"
                  className="wp-form-input"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="http://"
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* About Yourself */}
        <h2 className="wp-section-title">About Yourself</h2>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th>
                <label>Profile Picture</label>
              </th>
              <td>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  <div
                    onClick={handleAvatarClick}
                    style={{
                      width: '96px',
                      height: '96px',
                      borderRadius: '50%',
                      background: avatarPreview ? 'transparent' : '#c3c4c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '2px solid #c3c4c7',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2271b1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#c3c4c7';
                    }}
                  >
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%',
                        }}
                      />
                    ) : (
                      getInitials()
                    )}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: '#fff',
                        fontSize: '11px',
                        padding: '4px',
                        textAlign: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                      }}
                    >
                      Change
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <button
                        type="button"
                        onClick={handleAvatarClick}
                        className="wp-button-secondary"
                      >
                        Upload New Picture
                      </button>
                      {avatarPreview || formData.profilePicture ? (
                        <button
                          type="button"
                          onClick={handleRemoveProfilePicture}
                          className="wp-button-secondary"
                          style={{ backgroundColor: '#dc3232', color: '#fff', borderColor: '#dc3232' }}
                          disabled={saving}
                        >
                          Remove Picture
                        </button>
                      ) : null}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <p className="wp-info-text" style={{ margin: '8px 0 0 0' }}>
                      Upload a new profile picture. Recommended size: 200x200px. Maximum file size: 5MB.
                    </p>
                    {avatarError && (
                      <p style={{ color: '#d63638', fontSize: '13px', margin: '8px 0 0 0' }}>{avatarError}</p>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Account Management */}
        <h2 className="wp-section-title">Account Management</h2>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th>
                <label>New Password</label>
              </th>
              <td>
                {!showPasswordForm ? (
                  <button 
                    type="button" 
                    className="wp-button-primary"
                    onClick={() => setShowPasswordForm(true)}
                  >
                    Set New Password
                  </button>
                ) : (
                  <div style={{ maxWidth: '500px' }}>
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="wp-form-input"
                        style={{ 
                          paddingRight: '180px',
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Enter new password"
                        autoFocus
                      />
                      <div style={{ 
                        position: 'absolute', 
                        right: '8px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: '1px solid #00a32a',
                            borderRadius: '3px',
                            background: 'transparent',
                            color: '#00a32a',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span style={{ fontSize: '14px' }}>{showPassword ? '👁️' : '👁️‍🗨️'}</span>
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelPassword}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: '1px solid #00a32a',
                            borderRadius: '3px',
                            background: 'transparent',
                            color: '#00a32a',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    {newPassword && (
                      <div style={{ 
                        marginBottom: '8px',
                        height: '24px',
                        backgroundColor: passwordStrength.color,
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: passwordStrength.strength === 'strong' ? '#fff' : '#000',
                        width: '100%'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: '500' }}>
                          {passwordStrength.label}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={generateStrongPassword}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          border: '1px solid #00a32a',
                          borderRadius: '3px',
                          background: 'transparent',
                          color: '#00a32a',
                          cursor: 'pointer'
                        }}
                      >
                        Generate Strong Password
                      </button>
                      <button
                        type="button"
                        onClick={handlePasswordChange}
                        className="wp-button-primary"
                        disabled={changingPassword || !newPassword || newPassword.length < 6}
                      >
                        {changingPassword ? 'Changing...' : 'Set New Password'}
                      </button>
                    </div>
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th>
                <label>Sessions</label>
              </th>
              <td>
                <button type="button" className="wp-button-primary">
                  Log Out Everywhere Else
                </button>
                <p className="wp-info-text">
                  Did you lose your phone or leave your account logged in at a public computer? You can log out everywhere else, and stay logged in here.
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Application Passwords */}
        <h2 className="wp-section-title">Application Passwords</h2>
        <table className="wp-form-table">
          <tbody>
            <tr>
              <th colSpan="2">
                <p style={{ margin: 0, fontWeight: 'normal' }}>
                  Application passwords allow authentication via non-interactive systems, such as XML-RPC or the REST API, without providing your actual password. Application passwords can be easily revoked. They cannot be used for traditional logins to your website.
                </p>
              </th>
            </tr>
            <tr>
              <th>
                <label htmlFor="appPasswordName">New Application Password Name</label>
              </th>
              <td>
                <input
                  type="text"
                  id="appPasswordName"
                  className="wp-form-input"
                  placeholder="e.g., My iPhone"
                  value={newAppPasswordName}
                  onChange={(e) => setNewAppPasswordName(e.target.value)}
                  disabled={creatingAppPassword}
                />
                <p className="wp-info-text">
                  Required to create an Application Password, but not to update the user.
                </p>
                {newlyCreatedPassword && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f0f6fc',
                    border: '1px solid #2271b1',
                    borderRadius: '3px'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#2271b1' }}>
                      Your new application password:
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <code style={{
                        padding: '8px 12px',
                        backgroundColor: '#fff',
                        border: '1px solid #c3c4c7',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        flex: 1
                      }}>
                        {newlyCreatedPassword.password}
                      </code>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(newlyCreatedPassword.password);
                          setMessage({ type: 'success', text: 'Password copied to clipboard!' });
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          border: '1px solid #2271b1',
                          borderRadius: '3px',
                          background: 'transparent',
                          color: '#2271b1',
                          cursor: 'pointer'
                        }}
                      >
                        Copy
                      </button>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#646970' }}>
                      ⚠️ Make sure to copy this password now. You won't be able to see it again!
                    </p>
                  </div>
                )}
                <button 
                  type="button" 
                  className="wp-button-primary" 
                  style={{ marginTop: '10px' }}
                  onClick={handleCreateApplicationPassword}
                  disabled={creatingAppPassword || !newAppPasswordName.trim()}
                >
                  {creatingAppPassword ? 'Creating...' : 'Add Application Password'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Application Passwords List */}
        {applicationPasswords.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <table className="wp-form-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #c3c4c7' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #c3c4c7' }}>Created</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #c3c4c7' }}>Last Used</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #c3c4c7' }}>Last IP</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #c3c4c7' }}>Revoke</th>
                </tr>
              </thead>
              <tbody>
                {applicationPasswords.map((appPwd) => (
                  <tr key={appPwd.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #c3c4c7' }}>
                      <strong>{appPwd.name}</strong>
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #c3c4c7' }}>
                      {formatDate(appPwd.createdAt)}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #c3c4c7' }}>
                      {formatDate(appPwd.lastUsedAt)}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #c3c4c7' }}>
                      {appPwd.lastIP || '—'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #c3c4c7' }}>
                      <button
                        type="button"
                        onClick={() => handleRevokeApplicationPassword(appPwd.id)}
                        disabled={revokingPasswordId === appPwd.id}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          border: '1px solid #00a32a',
                          borderRadius: '3px',
                          background: 'transparent',
                          color: '#00a32a',
                          cursor: revokingPasswordId === appPwd.id ? 'not-allowed' : 'pointer',
                          opacity: revokingPasswordId === appPwd.id ? 0.6 : 1
                        }}
                      >
                        {revokingPasswordId === appPwd.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={handleRevokeAllApplicationPasswords}
                disabled={revokingAll}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  border: '1px solid #00a32a',
                  borderRadius: '3px',
                  background: 'transparent',
                  color: '#00a32a',
                  cursor: revokingAll ? 'not-allowed' : 'pointer',
                  opacity: revokingAll ? 0.6 : 1
                }}
              >
                {revokingAll ? 'Revoking...' : 'Revoke all application passwords'}
              </button>
            </div>
          </div>
        )}

        <p className="submit">
          <button type="submit" className="wp-button-primary" disabled={saving}>
            {saving ? 'Updating...' : 'Update Profile'}
          </button>
        </p>
      </form>
    </div>
  );
};

export default UserProfile;

